import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const LinkedInCallback = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const hasProcessedCallback = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (hasProcessedCallback.current) {
        return;
      }
      hasProcessedCallback.current = true;
      setStatus('processing');

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const storedState = sessionStorage.getItem('linkedin_auth_state');
      
      console.log('LinkedInCallback: Processing...', { code, state, errorParam, errorDescription, storedState });

      if (errorParam) {
        const message = `LinkedIn authentication error: ${errorDescription || errorParam}`;
        setErrorMsg(message);
        setStatus('error');
        navigate(`/dashboard?error=linkedin_auth_failed&details=${encodeURIComponent(message)}`, { replace: true });
        return;
      }
      
      if (!state || state !== storedState) {
        const message = 'Invalid state parameter. Authentication failed.';
        setErrorMsg(message);
        setStatus('error');
        navigate(`/dashboard?error=invalid_state&details=${encodeURIComponent(message)}`, { replace: true });
        return;
      }
      
      if (!code) {
        const message = 'No authorization code received from LinkedIn';
        setErrorMsg(message);
        setStatus('error');
        navigate(`/dashboard?error=no_code&details=${encodeURIComponent(message)}`, { replace: true });
        return;
      }
      
      sessionStorage.removeItem('linkedin_auth_state');

      try {
        let redirectUri = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:3000/linkedin-callback' 
          : process.env.REACT_APP_LINKEDIN_REDIRECT_URI;
        
        console.log('LinkedInCallback: Exchanging code using redirect URI for connect-linkedin:', redirectUri);
        
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/connect-linkedin`,
          { code, redirectUri },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        
        if (response.data.user && updateUser) {
          updateUser(response.data.user);
        }
        
        setStatus('success');
        navigate('/dashboard?linkedin=connected', { replace: true });

      } catch (apiError) {
        console.error('LinkedInCallback: API Error during connect-linkedin:', apiError);
        const message = apiError.response?.data?.message || 'Failed to connect LinkedIn account after token exchange.';
        setErrorMsg(message);
        setStatus('error');
        
        // Check if this is a "linkedin_already_connected" error
        if (apiError.response?.data?.code === 'linkedin_already_connected') {
          localStorage.setItem('linkedin_last_error', JSON.stringify(apiError.response.data));
          navigate('/onboarding', { replace: true });
        } else {
          navigate(`/dashboard?error=connection_failed&details=${encodeURIComponent(message)}`, { replace: true });
        }
      }
    };
    
    handleCallback();
  }, []);
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
        textAlign: 'center'
      }}
    >
      {status === 'processing' && (
        <>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Connecting your LinkedIn account...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please wait while we complete the process.
          </Typography>
        </>
      )}
      
      {status === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          LinkedIn account connected successfully! Redirecting to dashboard...
        </Alert>
      )}
      
      {status === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg || 'An error occurred during LinkedIn connection. Redirecting to dashboard...'}
        </Alert>
      )}
    </Box>
  );
};

export default LinkedInCallback; 